import { useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface ResizeHandleProps {
  position: 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'e' | 'w';
  onResize: (position: string, delta: { x: number; y: number }) => void;
  onResizeStart: () => void;
  onResizeEnd: () => void;
}

const POSITION_CLASSES = {
  nw: '-top-1 -left-1 cursor-nw-resize',
  ne: '-top-1 -right-1 cursor-ne-resize',
  sw: '-bottom-1 -left-1 cursor-sw-resize',
  se: '-bottom-1 -right-1 cursor-se-resize',
  n: '-top-1 left-1/2 -translate-x-1/2 cursor-n-resize',
  s: '-bottom-1 left-1/2 -translate-x-1/2 cursor-s-resize',
  e: 'top-1/2 -translate-y-1/2 -right-1 cursor-e-resize',
  w: 'top-1/2 -translate-y-1/2 -left-1 cursor-w-resize',
};

export function ResizeHandle({ position, onResize, onResizeStart, onResizeEnd }: ResizeHandleProps) {
  const [isDragging, setIsDragging] = useState(false);
  const startPos = useRef({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDragging(true);
    startPos.current = { x: e.clientX, y: e.clientY };
    onResizeStart();

    const handleMouseMove = (e: MouseEvent) => {
      const delta = {
        x: e.clientX - startPos.current.x,
        y: e.clientY - startPos.current.y,
      };
      onResize(position, delta);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      onResizeEnd();
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <div
      className={cn(
        "absolute w-5 h-5 bg-primary border-2 border-white rounded-full z-[60]",
        "opacity-100 transition-opacity",
        "hover:scale-125 transition-transform",
        "pointer-events-auto",
        POSITION_CLASSES[position],
        isDragging && "scale-125"
      )}
      onMouseDown={handleMouseDown}
      style={{ pointerEvents: 'auto' }}
    />
  );
}
